import axios from 'axios';

const API_BASE = '/api/work-orders';

export const orderService = {
  // Get all work orders with optional filters (pass { signal } for AbortController)
  getAll: async (params = {}, axiosConfig = {}) => {
    const response = await axios.get(API_BASE, { params, ...axiosConfig });
    return response.data;
  },

  /** Due dates + customer ship dates for inclusive YYYY-MM-DD range. */
  getCalendar: async ({ from, to }, axiosConfig = {}) => {
    const response = await axios.get(`${API_BASE}/calendar`, {
      params: { from, to },
      ...axiosConfig
    });
    return response.data;
  },

  // Get single work order by ID
  getById: async (id) => {
    const response = await axios.get(`${API_BASE}/${id}`);
    return response.data;
  },

  /** Customer packing slip rows per work order line (identifier, qty, shipped_date). */
  getCustomerPackingAllocations: async (workOrderId) => {
    const response = await axios.get(`${API_BASE}/${workOrderId}/customer-packing-allocations`);
    return response.data;
  },

  // Create new work order
  create: async (orderData) => {
    const response = await axios.post(API_BASE, orderData);
    return response.data;
  },

  // Update work order
  update: async (id, orderData) => {
    const response = await axios.put(`${API_BASE}/${id}`, orderData);
    return response.data;
  },

  /** Customer PO only; does not trigger order/quote revision. */
  updateCustomerPo: async (id, po_number) => {
    const response = await axios.patch(`${API_BASE}/${id}/customer-po`, { po_number });
    return response.data;
  },

  /** Line-item tax (percent or fixed). Syncs to linked quote without creating a quote revision. */
  updateTax: async (id, taxPayload) => {
    const response = await axios.patch(`${API_BASE}/${id}/tax`, taxPayload);
    return response.data;
  },

  // Delete work order
  delete: async (id) => {
    const response = await axios.delete(`${API_BASE}/${id}`);
    return response.data;
  },

  // Add line item
  addLineItem: async (orderId, itemData) => {
    const response = await axios.post(`${API_BASE}/${orderId}/line-items`, itemData);
    return response.data;
  },

  // Update line item
  updateLineItem: async (orderId, itemId, itemData) => {
    const response = await axios.put(`${API_BASE}/${orderId}/line-items/${itemId}`, itemData);
    return response.data;
  },

  // Delete line item
  deleteLineItem: async (orderId, itemId) => {
    const response = await axios.delete(`${API_BASE}/${orderId}/line-items/${itemId}`);
    return response.data;
  },

  // Unprocess order (set status to open)
  unprocess: async (orderId) => {
    const response = await axios.post(`${API_BASE}/${orderId}/unprocess`);
    return response.data;
  },

  /** Same quote/order family as GET /api/quotes/:id/lineage when this order has a quote */
  getLineage: async (orderId) => {
    const response = await axios.get(`${API_BASE}/${orderId}/lineage`);
    return response.data;
  },

  /** After order header/line changes, sync linked quote (deprecate old, create revision) when needed. */
  reviseLinkedQuote: async (orderId) => {
    const response = await axios.post(`${API_BASE}/${orderId}/revise-linked-quote`);
    return response.data;
  },

  /** Replace a quote-linked order via quote revision (retire old, create new). */
  reviseOrder: async (orderId) => {
    const response = await axios.post(`${API_BASE}/${orderId}/revise`);
    return response.data;
  },

  // Get full revision chain for an order (all predecessors, oldest first)
  getRevisions: async (orderId) => {
    const response = await axios.get(`${API_BASE}/${orderId}/revisions`);
    return response.data;
  },

  getProcurementSummary: async (orderId) => {
    const response = await axios.get(`${API_BASE}/${orderId}/procurement-summary`);
    return response.data;
  },

  getCompletionReadiness: async (orderId) => {
    const response = await axios.get(`${API_BASE}/${orderId}/completion-readiness`);
    return response.data;
  },

  /** Active pencil-edit lock for this order, or null. */
  getEditLock: async (orderId) => {
    const response = await axios.get(`${API_BASE}/${orderId}/edit-lock`);
    return response.data;
  },

  /** Acquire edit lock. 409 = held by someone else. */
  acquireEditLock: async (orderId) => {
    const response = await axios.post(`${API_BASE}/${orderId}/edit-lock`);
    return response.data;
  },

  /** Extend TTL for current holder only; 409 if lock missing or held by another user. */
  refreshEditLock: async (orderId) => {
    const response = await axios.post(`${API_BASE}/${orderId}/edit-lock/heartbeat`);
    return response.data;
  },

  /** Release own lock; pass { force: true } for admin to clear any lock. */
  releaseEditLock: async (orderId, { force = false } = {}) => {
    const response = await axios.delete(`${API_BASE}/${orderId}/edit-lock`, {
      params: force ? { force: '1' } : {}
    });
    return response.data;
  }
};
